import React, { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { createToastRailGesture } from "../../utils/toastRailGesture";

const EMPTY_SNAPSHOT = Object.freeze({
  activeCount: 0,
  visibleId: null,
  repeatCount: 1,
  canDismiss: true,
  action: null,
  expanded: false,
  paused: false,
});

// What a capsule renders once its own toast has left the rail — its last frame
// during the exit animation. Never the newest toast's state.
const DEPARTED_TOAST_STATE = Object.freeze({ repeatCount: 1, canDismiss: true, action: null });

const EMPTY_STORE = Object.freeze({
  subscribe: () => () => {},
  getSnapshot: () => EMPTY_SNAPSHOT,
});

function actionHandler(action) {
  if (!action || typeof action !== "object") return null;
  return action.onClick || action.onAction || action.handler || null;
}

/**
 * The rail keeps one snapshot for every live toast, so a capsule that does not
 * say which toast it is would render the newest toast's state. `toastId` is the
 * identity `renderContent` stamps on each capsule; callers that hand in a
 * ready-made snapshot and no id keep reading that snapshot directly.
 */
function toastStateFrom(rail, toastId) {
  if (toastId === undefined || toastId === null) return rail;
  const own = rail.toasts && typeof rail.toasts.get === "function" ? rail.toasts.get(toastId) : undefined;
  return own || DEPARTED_TOAST_STATE;
}

/**
 * Adds rail-owned affordances around a toast body without changing that body's
 * rendered content. The controller is optional so the adapter is also useful
 * with an already-read snapshot in focused integrations.
 */
export default function ToastRailContent({
  children,
  content,
  controller,
  snapshot,
  toastId,
  onDismiss,
  closeToast,
  toastProps,
}) {
  const store =
    controller && typeof controller.subscribe === "function" && typeof controller.getSnapshot === "function"
      ? controller
      : EMPTY_STORE;
  const storeSnapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const rail = snapshot || storeSnapshot || EMPTY_SNAPSHOT;
  const dismiss = onDismiss || closeToast;
  const canDismissThroughRail = typeof dismiss === "function";
  // react-toastify rebuilds a toast's `closeToast` on every `toast.update`, and
  // the rail controller retimes essentially every toast, so this callback's
  // identity is not stable. Reading it from a ref keeps the gesture instance —
  // and the pointer sequence it is tracking — alive across those re-renders.
  const dismissRef = useRef(dismiss);
  const dismissedRef = useRef(false);
  // The library's own distance drag closes through the same callback. Once it
  // has fired, the toast is already being removed and `isIn` is false, so the
  // rail must not ask for a second close.
  const closedElsewhere = Boolean(toastProps) && toastProps.isIn === false;
  const closedElsewhereRef = useRef(closedElsewhere);

  useEffect(() => {
    dismissRef.current = dismiss;
    closedElsewhereRef.current = closedElsewhere;
  }, [dismiss, closedElsewhere]);

  // The single point where the rail's velocity swipe and any other close for
  // this toast converge, so it is where dismissal is made idempotent.
  const requestDismiss = useCallback(() => {
    if (dismissedRef.current || closedElsewhereRef.current) return;
    const dismissNow = dismissRef.current;
    if (typeof dismissNow !== "function") return;
    dismissedRef.current = true;
    dismissNow();
  }, []);

  const own = toastStateFrom(rail, toastId);
  // A boolean, and this toast's own: an unrelated toast arriving can no longer
  // change it, so it cannot rebuild the gesture mid-swipe.
  const canDismissThisToast = own.canDismiss !== false;

  const gesture = useMemo(
    () =>
      createToastRailGesture({
        canDismiss: canDismissThisToast,
        onDismiss: canDismissThroughRail ? requestDismiss : undefined,
      }),
    [canDismissThisToast, canDismissThroughRail, requestDismiss]
  );
  const action = own.action;
  const onAction = actionHandler(action);
  const actionLabel = action && typeof action.label === "string" ? action.label : null;
  const source = content === undefined ? children : content;
  // The spec puts the overflow count on the one visible capsule, so a capsule
  // behind it in the stack does not repeat its own +N.
  const isVisibleCapsule = toastId === undefined || toastId === null || rail.visibleId === toastId;
  const additionalCount = isVisibleCapsule ? Math.max(0, (rail.activeCount || 0) - 1) : 0;
  const repeatCount = Math.max(1, own.repeatCount || 1);

  return (
    <div
      className="toast-rail-content"
      data-testid="toast-rail-content"
      data-expanded={rail.expanded ? "true" : "false"}
      data-paused={rail.paused ? "true" : "false"}
      {...gesture}
    >
      <div className="toast-rail-content__source">{source}</div>
      {additionalCount > 0 && (
        <span className="toast-rail-content__count" aria-label={`${additionalCount} additional notifications`}>
          +{additionalCount}
        </span>
      )}
      {repeatCount > 1 && (
        <span className="toast-rail-content__repeat" aria-label={`Repeated ${repeatCount} times`}>
          ×{repeatCount}
        </span>
      )}
      {actionLabel && typeof onAction === "function" && (
        <button
          type="button"
          className="toast-rail-content__action"
          aria-label={action.ariaLabel || actionLabel}
          onClick={(event) => {
            event.stopPropagation();
            onAction(event);
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
